import WidgetModel, { SendDataConfig } from '@mm/state/model/WidgetModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import { StoreState } from '@mm/state/store-interfaces';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import ConnectionModel, {
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import React, { ReactNode, useEffect } from 'react';
import classNames from 'classnames';
import { Handle, Position, NodeProps } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import common from '../index.module.css';
import css from './JSONWebhook.module.css';
import { autoIncrementedName } from '../../../../../../../../../common/helpers/name-autoincrement';
import { UNTITLED_CONNECTION_NAME } from '../../../../../../../connections/panels/ConnectionsPanel/ConnectionsPanel';
import { useConnectionWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { Count } from '../elements/Count/Count';
import { useOpenOnReady } from '../use-open-on-ready';
import { uniqueId } from 'lodash';
import { WebhookIcon } from '@mm/icons';

type Props = { className?: string } & NodeProps<{
  widget: WidgetModel;
}>;

export const JSONWebhook: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
}) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;
  const config = widget.config as Partial<SendDataConfig>;
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const { retrieveWidgetConnections, create } = useConnectionCRUDActions();
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const viewActions = useViewActions();
  const { connectionWebhookId } = config;

  const connections =
    useSelector((state: StoreState) => state.connections.widgetConnections) ||
    [];

  const getJSONWebhookConnections = (state: StoreState) =>
    state.connections.widgetConnections?.filter(
      connection => connection.kind === ConnectionKind.WIDGET,
    );
  const connectionsToShow = connections.filter(
    c => !c.sendDataWidgetConfig?.resourceSubscriptionTriggerName,
  );
  const selectedConnection =
    connectionsToShow &&
    connectionsToShow.find(c => c.id === connectionWebhookId);

  useEffect(() => {
    if (activeFbPageId && !connections.length) {
      retrieveWidgetConnections(activeFbPageId);
    }
  }, [activeFbPageId]);

  const onRemoveConnection = async () => {
    const clonedWidget = {
      ...widget,
      config: {
        connectionWebhookId: null,
      },
    };
    await connectionWidgetHandlers.update(clonedWidget);
  };

  const onConnectionChange = (connection: ConnectionModel) => {
    const { sendDataWidgetConfig: config } = connection;
    if (config) {
      const clonedWidget = {
        ...widget,
        config: {
          ...config,
          kind: 'zapier' as any,
        },
      };
      connectionWidgetHandlers.update(clonedWidget);
    }
  };

  const onEditConnection = (selectedConnectionId: number) => {
    viewActions.showSlideoutMenu('editJSONWebhookIntegrationEditor', {
      webhookId: selectedConnectionId,
      deleteAction: onRemoveConnection,
    });
  };

  const onAddNewConnection = async () => {
    const name = autoIncrementedName(
      UNTITLED_CONNECTION_NAME,
      connections.map(c => c.name),
    );

    const newConnection = await create(activeFbPageId, {
      name,
      connectionType: 'Webhook',
      kind: ConnectionKind.WIDGET,
      description: '',
    });
    await retrieveWidgetConnections(activeFbPageId);

    if (newConnection) {
      viewActions.showSlideoutMenu('editJSONWebhookIntegrationEditor', {
        webhookId: newConnection.id,
        deleteAction: onRemoveConnection,
      });
      onConnectionChange(newConnection);
    }
  };

  const onClick = () => {
    if (isDragging) {
      return;
    }
    viewActions.showSlideoutMenu('connectionsEditor', {
      title: 'JSON Webhooks Integration',
      widgetId: data.widget.id,
      selector: getJSONWebhookConnections,
      onConnectionChange,
      onEditConnection,
      onAddNewConnection,
    });
  };

  useOpenOnReady(widget, onClick);

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div
        className={classNames(className, common.card, css.container)}
        onClick={onClick}
      >
        <div className={css.body}>
          <div className={css.header}>
            <div className={css.icon}>
              <WebhookIcon />
            </div>

            <div>
              {React.Children.map<ReactNode, ReactNode>(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    // @ts-ignore onEdit does not seemto be present or types are off in @types/react
                    onEdit: onClick,
                    key: uniqueId(),
                  });
                }
              })}
            </div>

            <span className={css.placeholder}>JSON Webhook</span>
          </div>
          {selectedConnection && (
            <div className={css.info}>
              <span className={css.connectionName}>
                {selectedConnection.name}
              </span>

              <Count
                className={css.firesCount}
                value={`${data.widget.config?.firesCount || 0} contacts`}
              />
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
};
